import React from 'react';
import {styled} from 'baseui';
import {GatsbyImage} from 'gatsby-plugin-image';
import {graphql, useStaticQuery, Link} from 'gatsby';
import {Pressable} from '../landingPageSections/support/MainShortcuts';
import {handleMixpanelAnalytics, MixpanelEvents} from '../utils/events';

const Wrapper = styled('div', ({$theme}) => ({
  padding: '50px 40px',
  display: 'flex',
  flexDirection: 'row',
  minHeight: '400px',
  backgroundColor: $theme.colors.primary200,
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  fontWeight: '500',
  [$theme.mediaQuery.small]: {
    padding: '45px 20px',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
}));

const LogoWrapper = styled('div', ({$theme}) => ({
  marginRight: '34px',
  marginTop: '-16px',
  width: '128px',
  height: '64px',
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  [$theme.mediaQuery.small]: {
    width: '100%',
    margin: '0 0 40px 0',
    justifyContent: 'flex-start',
  },
}));

const FooterMenuWrapper = styled('div', ({$theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '84px',
  [$theme.mediaQuery.medium]: {
    gap: '34px',
  },
  [$theme.mediaQuery.small]: {
    gap: '0',
    margin: '0 0 40px 0',
    justifyContent: 'space-between',
    alignItems: 'start',
    width: '100%',
  },
}));

const FooterMenuSection = styled('div', ({$theme}: any) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  whiteSpace: 'nowrap',
  [$theme.mediaQuery.small]: {
    margin: '0',
    whiteSpace: 'normal',
  },
}));

const StyledTitle = styled('p', ({$theme}: any) => ({
  marginBottom: '23px',
  color: $theme.colors.backgroundPrimary,
  fontSize: '20px',
  fontWeight: 700,
}));

const StyledLink = styled(Link, ({$theme}: any) => ({
  marginBottom: '17px',
  color: $theme.colors.backgroundPrimary,
  textDecoration: 'none',
  fontSize: '14px',
  ':hover': {
    textDecoration: 'underline',
  },
  [$theme.mediaQuery.small]: {
    flexDirection: 'column',
  },
}));

const StyledPressable = styled(Pressable, () => ({
  marginBottom: '17px',
  textAlign: 'left',
}));

const StyledExternalLink = styled('a', ({$theme}) => ({
  marginBottom: '17px',
  color: $theme.colors.backgroundPrimary,
  textDecoration: 'none',
  fontSize: '14px',
  ':hover': {
    textDecoration: 'underline',
  },
  [$theme.mediaQuery.small]: {
    flexDirection: 'column',
  },
}));

const FooterSocialMenu = styled('div', ({$theme}: any) => ({
  margin: '0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [$theme.mediaQuery.small]: {
    width: '100%',
    justifyContent: 'flex-start',
  },
}));

const SocialLinkWrapper = styled('a', ({$theme}) => ({
  margin: '0 13px 36px 0',
  width: '33px',
  height: '33px',
  transition: '500ms ease',
  [$theme.mediaQuery.small]: {
    margin: '0 13px 0px 0',
  },
  ':hover': {
    opacity: '0.5',
  },
}));

const CopyrightWrapper = styled('div', ({$theme}: any) => ({
  padding: '0 40px',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: $theme.colors.primary200,
  [$theme.mediaQuery.small]: {
    padding: '0 20px',
    width: '100%',
    justifyContent: 'flex-start',
  },
}));

const CopyrightTitle = styled('p', ({$theme}: any) => ({
  fontSize: '14px',
  color: $theme.colors.backgroundPrimary,
}));

export default function Footer() {
  const data = useStaticQuery<GatsbyTypes.FooterQueryQuery>(graphql`
    query FooterQuery {
      imagePositive: contentfulAsset(title: {eq: "orca-mark"}) {
        title
        description
        gatsbyImageData(width: 120)
      }
      imageNegative: contentfulAsset(title: {eq: "orca-mark-negative"}) {
        title
        description
        gatsbyImageData(width: 120)
      }
      allContentfulPage {
        edges {
          node {
            slug
            title
            visibleInFooter
            type
          }
        }
      }
      contentfulHeaderAndFooter {
        infoTitle
        legalTitle
        copyrightTitle
        newsletterPageTitle
        socialMediaLink
        blogPageTitle
        jobsPageTitle
        productDetails
        instagramImage {
          gatsbyImageData(width: 60)
          description
        }
        facebookImage {
          gatsbyImageData(width: 60)
          description
        }
        linkedinImage {
          gatsbyImageData(width: 60)
          description
        }
      }
    }
  `);

  const {imageNegative} = data;
  const {
    infoTitle,
    legalTitle,
    copyrightTitle,
    socialMediaLink,
    instagramImage,
    facebookImage,
    linkedinImage,
    blogPageTitle,
    jobsPageTitle,
    productDetails,
  } = data.contentfulHeaderAndFooter!;

  const {edges} = data.allContentfulPage;
  const footerPages = edges.filter(({node}) => node.visibleInFooter);
  const legalPages = footerPages.filter(({node}) => node.type === 'legal');
  const infoPages = footerPages.filter(({node}) => node.type === 'plain');
  const year = new Date().getFullYear();

  const socialMediaImages = [instagramImage, facebookImage, linkedinImage];
  const socialEntries = socialMediaLink!.map((link, index) => ({
    link,
    image: socialMediaImages[index],
  }));

  const orcaLogo = imageNegative!;

  const onClickInfoPages = (slug: string | null) => {
    if (slug === '/contact/') {
      handleMixpanelAnalytics(MixpanelEvents.CONTACT_ENTERED);
    }
  };

  const onClickSocials = (link: string | null) => {
    const platformName = link ? new URL(link).hostname : '';
    handleMixpanelAnalytics(MixpanelEvents.SOCIAL_MEDIA_CLICKED, {
      social_media_type: platformName,
    });
  };

  return (
    <>
      <Wrapper>
        <LogoWrapper>
          <GatsbyImage
            loading='lazy'
            image={orcaLogo.gatsbyImageData}
            style={{height: '100%'}}
            alt={orcaLogo.description}
            imgStyle={{
              objectFit: 'contain',
            }}
          />
        </LogoWrapper>
        <FooterMenuWrapper>
          <FooterMenuSection>
            <StyledTitle>{infoTitle}</StyledTitle>
            <StyledExternalLink href='https://help.getorca.com/'>
              {productDetails}
            </StyledExternalLink>
            <StyledLink to='/chart-viewer/'>Chart Coverage</StyledLink>
            <StyledLink to='/account/sign-in/'>My Account</StyledLink>
            <StyledLink to='/blog/'>News</StyledLink>
            <StyledLink to='/changelog/'>Release notes</StyledLink>
            <StyledLink to='/careers/'>{jobsPageTitle}</StyledLink>
            {infoPages.map(({node}) => (
              <StyledPressable
                onClick={() => onClickInfoPages(node.slug)}
                key={node.slug}
              >
                <StyledLink to={node.slug}>{node.title}</StyledLink>
              </StyledPressable>
            ))}
          </FooterMenuSection>
          <FooterMenuSection>
            <StyledTitle>{legalTitle}</StyledTitle>
            {legalPages.map(({node}) => (
              <StyledLink key={node.slug} to={node.slug}>
                {node.title}
              </StyledLink>
            ))}
          </FooterMenuSection>
        </FooterMenuWrapper>
        <FooterSocialMenu>
          {socialEntries.map(({link, image}) => (
            <SocialLinkWrapper
              href={link}
              target='_blank'
              key={link}
              onClick={() => onClickSocials(link)}
            >
              <GatsbyImage
                loading='lazy'
                image={image?.gatsbyImageData}
                style={{width: '100%'}}
                alt={image?.description}
                imgStyle={{
                  objectFit: 'contain',
                }}
              />
            </SocialLinkWrapper>
          ))}
        </FooterSocialMenu>
      </Wrapper>
      <CopyrightWrapper>
        <CopyrightTitle>{`${year} ${copyrightTitle}`}</CopyrightTitle>
      </CopyrightWrapper>
    </>
  );
}
